import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FiArrowLeft, FiUpload, FiFileText, FiCheckCircle,
  FiPlus, FiList, FiX, FiExternalLink, FiAlertCircle, FiEdit2, FiSlash,
} from 'react-icons/fi';
import API from '../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import EditReceiptModal from '../components/common/EditReceiptModal';
import TdsFields, { EMPTY_TDS } from '../components/common/TdsFields';
import { resolveTds, allocationsPayload, tdsPayload } from '../utils/tds';
import './SalesOrders.css';

/* ── Helpers ─────────────────────────────────────────────────────── */
const money = (n) =>
  `₹${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmt = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const BILL_STATUS = {
  generated: { label: 'Unpaid',  color: '#dc2626', bg: '#fee2e2' },
  partial:   { label: 'Partial', color: '#d97706', bg: '#fef3c7' },
  paid:      { label: 'Paid',    color: '#16a34a', bg: '#dcfce7' },
  cancelled: { label: 'Cancelled', color: '#4b5563', bg: '#e5e7eb' },
};

const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 };
const inputStyle = {
  width: '100%', boxSizing: 'border-box', padding: '8px 10px',
  border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, outline: 'none',
};

const card = {
  background: '#fff', border: '1px solid #e5e7eb',
  borderRadius: 12, padding: 20, marginBottom: 20,
};

/* Resolve a stored pdfUrl (may be a bare /uploads/… path) to a full URL
   served by the backend, so it works regardless of the frontend origin. */
const API_ORIGIN = (() => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL.replace('/api', '');
  }
  if (window.location.hostname === 'localhost') {
    return 'http://localhost:5000';
  }
  // On production the frontend is on angelprinters.com but files are
  // served by the backend on api.angelprinters.com — derive it from
  // the current hostname (e.g. angelprinters.com → api.angelprinters.com).
  const host = window.location.hostname.replace(/^www\./, '');
  return `https://api.${host}`;
})();

const resolvePdfUrl = (url) => {
  if (!url) return url;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${API_ORIGIN}${url}`;
};

/* ── Log Payment Modal ───────────────────────────────────────────── */

/* ── Record Payment Modal ────────────────────────────────────────────
 * Replaced RecordPaymentModal. Creates a ClientReceipt instead of calling
 * POST /api/bills/:id/payment directly, so the ledger sees the payment.
 * ─────────────────────────────────────────────────────────────────── */
const RecordPaymentModal = ({ bill, onClose, onSaved }) => {
  const today = () => new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ mode: 'cash', bankAccountId: '', date: today(), note: '' });
  const [amount, setAmount]           = useState('');
  const [nextNumber, setNextNumber]   = useState('');
  const [bankAccounts, setBankAccounts] = useState([]);
  const [saving, setSaving]           = useState(false);
  const [tdsInput, setTdsInput]       = useState(EMPTY_TDS);
  const set = (f, v) => setForm(p => ({ ...p, [f]: v }));

  const balance = Number(bill.grandTotal) - Number(bill.amountReceived);

  // TDS (optional): % and amount are linked — see utils/tds.js
  const resolved = resolveTds({ bills: [bill], effective: Number(amount) || 0, mode: 'fifo', input: tdsInput });
  const settleNow = (Number(amount) || 0) + resolved.amount;
  // Save is blocked when TDS cannot be saved as entered, or payment + TDS is more than the balance
  const blocked   = !!resolved.error || (resolved.amount > 0 && settleNow > balance + 0.01);

  useEffect(() => {
    API.get('/company-bank-accounts', { params: { active: 'true' } })
      .then(({ data }) => setBankAccounts(data.accounts || []))
      .catch(() => {});
    API.get('/client-receipts/next-number')
      .then(({ data }) => setNextNumber(data.receiptNumber || ''))
      .catch(() => {});
  }, []);

  const handleSave = async () => {
    const amt = Number(amount) || 0;
    if (amt < 0) { toast.error('Enter a valid amount'); return; }
    if (amt <= 0 && resolved.amount <= 0) { toast.error('Enter the amount received or the TDS deducted'); return; }
    if (resolved.error) { toast.error(resolved.error); return; }
    if (settleNow > balance + 0.01) {
      toast.error(resolved.amount > 0
        ? `Payment ${money(amt)} + TDS ${money(resolved.amount)} is ${money(settleNow - balance)} more than the balance (${money(balance)}). If the customer deducted TDS, enter ${money(resolved.cashNeeded)} as the amount received.`
        : `Exceeds balance (max ${money(balance)} including TDS)`);
      return;
    }
    try {
      setSaving(true);
      const payload = {
        businessId:       bill.businessId,
        customerName:     bill.customerName,
        customerPhone:    bill.customerPhone || '',
        companyName:      bill.companyName || '',
        totalAmount:      amt,
        creditUsed:       0,
        receiptDate:      form.date,
        mode:             form.mode,
        bankAccountId:    form.bankAccountId || null,
        bankAccountLabel: bankAccounts.find(a => String(a.id) === String(form.bankAccountId))?.account_name || null,
        note:             form.note,
        billAllocations:  allocationsPayload(resolved.plan),
        ...tdsPayload(resolved, tdsInput, tdsInput.section),
      };
      await API.post('/client-receipts', payload);
      toast.success(`Payment recorded — Receipt ${nextNumber}`);
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to record payment');
    } finally { setSaving(false); }
  };

  const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: '#6b7280', marginBottom: 4 };
  const inputStyle = { width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, outline: 'none' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: 14, padding: 28, width: 520, maxWidth: '95vw', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
        <h3 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 700 }}>Record Payment</h3>
        <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 6 }}>
          {bill.billNumber} · {bill.companyName || bill.customerName}
        </div>
        {nextNumber && (
          <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 14, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, padding: '5px 10px', display: 'inline-block' }}>
            Receipt No: <strong style={{ color: '#16a34a' }}>{nextNumber}</strong> (auto-assigned)
          </div>
        )}
        <div style={{ display: 'flex', gap: 16, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '8px 14px', marginBottom: 16, fontSize: 13 }}>
          <span style={{ color: '#16a34a' }}>Received: <strong>{money(bill.amountReceived)}</strong></span>
          <span style={{ color: '#dc2626' }}>Balance: <strong>{money(balance)}</strong></span>
          <span style={{ color: '#111' }}>Total: <strong>{money(bill.grandTotal)}</strong></span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <div>
            <label style={labelStyle}>Amount Received (₹) *</label>
            <input type="number" min="0" max={balance} step="0.01"
              value={amount} onChange={e => setAmount(e.target.value)}
              placeholder={`Max ${money(balance)}`} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Date</label>
            <input type="date" value={form.date} onChange={e => set('date', e.target.value)}
              onClick={e => e.currentTarget.showPicker?.()} style={{ ...inputStyle, cursor: 'pointer' }} />
          </div>
          <div>
            <label style={labelStyle}>Mode</label>
            <select value={form.mode} onChange={e => { set('mode', e.target.value); set('bankAccountId', ''); }} style={inputStyle}>
              <option value="cash">Cash</option>
              <option value="upi">UPI</option>
              <option value="cheque">Cheque</option>
              <option value="bank_transfer">Bank Transfer</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>Note</label>
            <input type="text" value={form.note} onChange={e => set('note', e.target.value)} placeholder="Optional" style={inputStyle} />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={labelStyle}>Received In Account</label>
            <select value={form.bankAccountId} onChange={e => set('bankAccountId', e.target.value)} style={inputStyle}>
              <option value="">— Select Account —</option>
              {bankAccounts
                .filter(a => {
                  if (form.mode === 'cash')         return a.account_type === 'cash';
                  if (form.mode === 'upi')          return a.account_type === 'upi';
                  if (form.mode === 'bank_transfer' || form.mode === 'cheque') return a.account_type === 'bank';
                  return true;
                })
                .map(a => (
                  <option key={a.id} value={a.id}>
                    {a.account_name}{a.account_number_last4 ? ` (••••${a.account_number_last4})` : ''}
                  </option>
                ))
              }
            </select>
          </div>
        </div>
        <TdsFields input={tdsInput} onChange={setTdsInput} resolved={resolved} overflowMode="block" />
        {settleNow > 0 && (
          <div style={{ marginTop: 10, fontSize: 12.5, color: settleNow > balance + 0.01 ? '#dc2626' : '#374151' }}>
            Settles <strong>{money(settleNow)}</strong> of this bill
            {resolved.amount > 0 && <> (bank {money(Number(amount) || 0)} + TDS {money(resolved.amount)})</>}
            {' '}· balance after: <strong>{money(Math.max(0, balance - settleNow))}</strong>
          </div>
        )}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
          <button onClick={onClose} style={{ padding: '9px 20px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#f9fafb', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
          <button onClick={handleSave} disabled={saving || blocked}
            style={{ padding: '9px 20px', borderRadius: 8, border: 'none', background: 'var(--primary)', color: '#fff', fontWeight: 600, fontSize: 13, cursor: (saving || blocked) ? 'not-allowed' : 'pointer', opacity: (saving || blocked) ? 0.5 : 1 }}>
            {saving ? 'Saving…' : 'Save Payment'}
          </button>
        </div>
      </div>
    </div>
  );
};


/* ── PDF Upload Modal ────────────────────────────────────────────── */
const PdfUploadModal = ({ bill, onClose, onUploaded }) => {
  const [file, setFile]       = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const handleUpload = async () => {
    if (!file) { toast.error('Select a PDF file'); return; }
    try {
      setUploading(true);
      // Step 1: upload PDF to BunnyCDN
      const fd = new FormData();
      fd.append('pdf', file);
      const { data: uploadData } = await API.post('/upload/bill-pdf', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (!uploadData.url) throw new Error('No URL returned from upload');
      // Step 2: save the CDN URL on the bill record
      const { data } = await API.put(`/bills/${bill.id}`, { pdfUrl: uploadData.url });
      toast.success('PDF uploaded');
      onUploaded(data.bill);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Upload failed');
    } finally { setUploading(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: 14, padding: 28, width: 420, maxWidth: '95vw', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Upload Bill PDF</h3>
          <button onClick={onClose} style={{ background: '#f3f4f6', border: 'none', borderRadius: 8, padding: '6px 8px', cursor: 'pointer' }}><FiX size={16} /></button>
        </div>
        <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>
          Attach the generated PDF for <strong>{bill.billNumber}</strong>.
        </div>
        <div
          onClick={() => fileRef.current?.click()}
          style={{
            border: '2px dashed #d1d5db', borderRadius: 10, padding: '28px 20px',
            textAlign: 'center', cursor: 'pointer', background: '#fafafa',
            marginBottom: 16, transition: 'border-color 0.2s',
          }}
        >
          <FiUpload size={24} style={{ color: '#9ca3af', marginBottom: 8 }} />
          <div style={{ fontSize: 13, color: '#6b7280' }}>
            {file ? <strong style={{ color: '#111' }}>{file.name}</strong> : 'Click to select PDF (max 10 MB)'}
          </div>
          <input ref={fileRef} type="file" accept="application/pdf" style={{ display: 'none' }}
            onChange={e => setFile(e.target.files[0] || null)} />
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '9px 20px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#f9fafb', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>Cancel</button>
          <button onClick={handleUpload} disabled={uploading || !file}
            style={{ padding: '9px 20px', borderRadius: 8, border: 'none', background: 'var(--primary)', color: '#fff', fontWeight: 600, fontSize: 13, cursor: 'pointer', opacity: uploading || !file ? 0.6 : 1 }}>
            {uploading ? 'Uploading…' : 'Upload PDF'}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── Edit Bill Modal ─────────────────────────────────────────────── */
const EditBillModal = ({ bill, onClose, onSaved }) => {
  const [form, setForm] = useState({
    billNumber: bill.billNumber || '',
    billDate:   bill.billDate   || '',
    dueDate:    bill.dueDate    || '',
    notes:      bill.notes      || '',
    poNumber:   bill.poNumber   || '',
    poDate:     bill.poDate     || '',
  });
  const [poNumError, setPoNumError]     = useState('');
  const [poNumChecking, setPoChecking] = useState(false);
  const [saving, setSaving]       = useState(false);
  const [numError, setNumError]   = useState('');
  const [numChecking, setChecking] = useState(false);
  const set = (f, v) => setForm(p => ({ ...p, [f]: v }));

  const checkBillNumber = async (val) => {
    if (!val.trim() || val.trim() === bill.billNumber) { setNumError(''); return; }
    setChecking(true);
    try {
      const { data } = await API.get(`/bills/check-number?billNumber=${encodeURIComponent(val.trim())}`);
      setNumError(data.exists ? `Bill number "${val.trim()}" is already in use.` : '');
    } catch { /* ignore */ } finally { setChecking(false); }
  };

  const checkPoNumber = async (val) => {
    if (!val.trim() || val.trim() === bill.poNumber) { setPoNumError(''); return; }
    setPoChecking(true);
    try {
      const params = new URLSearchParams({ poNumber: val.trim(), excludeBillId: bill.id });
      if (bill.businessId) params.set('businessId', bill.businessId);
      else if (bill.customerName) params.set('customerName', bill.customerName);
      const { data } = await API.get(`/bills/check-po-number?${params}`);
      setPoNumError(data.exists ? `PO number "${val.trim()}" is already used on bill ${data.bill?.billNumber || ''} for this customer.` : '');
    } catch { /* ignore */ } finally { setPoChecking(false); }
  };

  const handleSave = async () => {
    if (!form.billNumber.trim()) { toast.error('Bill number is required'); return; }
    if (numError) { toast.error(numError); return; }
    if (poNumError) { toast.error(poNumError); return; }
    try {
      setSaving(true);
      const { data } = await API.put(`/bills/${bill.id}`, {
        billNumber: form.billNumber.trim(),
        billDate:   form.billDate   || undefined,
        dueDate:    form.dueDate    || null,
        notes:      form.notes      || '',
        poNumber:   form.poNumber.trim() || null,
        poDate:     form.poDate     || null,
      });
      toast.success('Bill updated');
      onSaved(data.bill);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update bill');
    } finally { setSaving(false); }
  };

  const overlayStyle = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
    zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center',
  };
  const boxStyle = {
    background: '#fff', borderRadius: 14, padding: 28,
    width: 520, maxWidth: '95vw', boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
  };

  return (
    <div style={overlayStyle}>
      <div style={boxStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Edit Bill</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}>
            <FiX size={18} />
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
          {/* Bill Number */}
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={labelStyle}>Bill Number *</label>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                value={form.billNumber}
                onChange={e => { set('billNumber', e.target.value); setNumError(''); }}
                onBlur={e => checkBillNumber(e.target.value)}
                style={{ ...inputStyle, borderColor: numError ? '#dc2626' : '#e5e7eb', paddingRight: numChecking ? 32 : undefined }}
              />
              {numChecking && (
                <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', width: 13, height: 13, borderRadius: '50%', border: '2px solid #3b82f6', borderTopColor: 'transparent', display: 'inline-block', animation: 'spin 0.8s linear infinite' }} />
              )}
            </div>
            {numError && <div style={{ marginTop: 4, fontSize: 12, color: '#dc2626' }}>⚠️ {numError}</div>}
          </div>

          {/* Bill Date */}
          <div>
            <label style={labelStyle}>Bill Date</label>
            <input type="date" value={form.billDate} onChange={e => set('billDate', e.target.value)} style={inputStyle} />
          </div>

          {/* Due Date */}
          <div>
            <label style={labelStyle}>Due Date <span style={{ fontWeight: 400, color: '#9ca3af' }}>(optional)</span></label>
            <div style={{ position: 'relative' }}>
              <input type="date" value={form.dueDate} onChange={e => set('dueDate', e.target.value)} style={inputStyle} />
              {form.dueDate && (
                <button onClick={() => set('dueDate', '')}
                  style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', display: 'flex', alignItems: 'center' }}>
                  <FiX size={13} />
                </button>
              )}
            </div>
          </div>

          {/* PO Fields */}
          <div style={{ gridColumn: '1 / -1', borderTop: '1px solid #f3f4f6', paddingTop: 14, marginTop: 4 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 }}>Purchase Order</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={labelStyle}>PO Number</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    value={form.poNumber}
                    onChange={e => { set('poNumber', e.target.value); setPoNumError(''); }}
                    onBlur={e => checkPoNumber(e.target.value)}
                    placeholder="e.g. PO-2026-001"
                    style={{ ...inputStyle, borderColor: poNumError ? '#dc2626' : '#e5e7eb', paddingRight: poNumChecking ? 32 : undefined }}
                  />
                  {poNumChecking && <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', width: 13, height: 13, borderRadius: '50%', border: '2px solid #3b82f6', borderTopColor: 'transparent', display: 'inline-block', animation: 'spin 0.8s linear infinite' }} />}
                </div>
                {poNumError && <div style={{ marginTop: 4, fontSize: 12, color: '#dc2626' }}>⚠️ {poNumError}</div>}
              </div>
              <div>
                <label style={labelStyle}>PO Date</label>
                <input type="date" value={form.poDate} onChange={e => set('poDate', e.target.value)} onClick={e => e.currentTarget.showPicker?.()} style={{ ...inputStyle, cursor: 'pointer' }} />
              </div>
            </div>
          </div>

          {/* Notes */}
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={labelStyle}>Notes</label>
            <textarea
              value={form.notes}
              onChange={e => set('notes', e.target.value)}
              rows={3}
              placeholder="Any remarks for this bill…"
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose}
            style={{ padding: '9px 20px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#f9fafb', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving || !!numError || numChecking || !!poNumError || poNumChecking}
            style={{ padding: '9px 22px', borderRadius: 8, border: 'none', background: 'var(--primary)', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer', opacity: (saving || numChecking || poNumChecking) ? 0.7 : 1 }}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );
};

/* ── Cancel Bill Modal ───────────────────────────────────────────────
 * Step 1: asks the server what WOULD happen (GET /bills/:id/cancel-preview)
 *         and shows where every rupee goes. Nothing is changed yet.
 * Step 2: admin types a reason and clicks Confirm (POST /bills/:id/cancel).
 * ─────────────────────────────────────────────────────────────────── */
const OUTCOME_STYLE = {
  cancelled: { label: 'Cancelled',        color: '#b91c1c', bg: '#fee2e2' },
  to_order:  { label: 'Back to order',    color: '#1d4ed8', bg: '#dbeafe' },
  to_wallet: { label: 'Back to wallet',   color: '#047857', bg: '#d1fae5' },
  removed:   { label: 'Removed',          color: '#b45309', bg: '#fef3c7' },
};

const CancelBillModal = ({ bill, onClose, onCancelled }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [preview, setPreview] = useState(null);
  const [reason, setReason]   = useState('');
  const [saving, setSaving]   = useState(false);

  useEffect(() => {
    let alive = true;
    API.get(`/bills/${bill.id}/cancel-preview`)
      .then(({ data }) => { if (alive) setPreview(data.preview); })
      .catch((err) => { if (alive) setError(err.response?.data?.message || 'Could not check this bill'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [bill.id]);

  const blockers   = preview?.blockers || [];
  const groups     = preview?.groups || [];
  const lines      = preview?.lines || [];
  const hasDirect  = groups.some(g => g.type === 'direct_receipt');
  const canConfirm = !!preview && blockers.length === 0 && reason.trim().length > 0 && !saving;

  const confirm = async () => {
    try {
      setSaving(true);
      const { data } = await API.post(`/bills/${bill.id}/cancel`, {
        reason: reason.trim(),
        fingerprint: preview.fingerprint,
      });
      toast.success(`Bill cancelled — ${data.originalBillNumber} can be used again`);
      onCancelled(data.bill);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not cancel the bill');
      setSaving(false);
    }
  };

  const th = { padding: '8px 10px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' };
  const td = { padding: '10px', verticalAlign: 'top', borderTop: '1px solid #f3f4f6', fontSize: 13 };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: 14, padding: 26, width: 680, maxWidth: '95vw', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#991b1b', display: 'flex', alignItems: 'center', gap: 8 }}>
              <FiSlash size={17} /> Cancel Bill {bill.billNumber}
            </h3>
            <div style={{ fontSize: 13, color: '#6b7280', marginTop: 3 }}>
              {bill.companyName || bill.customerName} · Total {money(bill.grandTotal)}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280' }}><FiX size={18} /></button>
        </div>

        {loading && <div style={{ padding: 30, textAlign: 'center', color: '#6b7280' }}>Checking what will happen…</div>}
        {error && <div style={{ padding: 12, borderRadius: 8, background: '#fee2e2', color: '#991b1b', fontSize: 13, marginTop: 12 }}>{error}</div>}

        {preview && (
          <>
            {/* Blockers */}
            {blockers.length > 0 && (
              <div style={{ margin: '14px 0', padding: '12px 14px', borderRadius: 8, background: '#fee2e2', border: '1px solid #fecaca', color: '#991b1b', fontSize: 13 }}>
                <div style={{ fontWeight: 700, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}><FiAlertCircle size={14} /> This bill cannot be cancelled yet</div>
                {blockers.map((b, i) => <div key={i} style={{ marginTop: 3 }}>• {b}</div>)}
              </div>
            )}

            {/* A. Payments */}
            <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', margin: '16px 0 6px' }}>1. Payments on this bill — where each amount goes</div>
            {groups.length === 0 ? (
              <div style={{ padding: '10px 12px', borderRadius: 8, background: '#f9fafb', color: '#6b7280', fontSize: 13 }}>No payments on this bill.</div>
            ) : (
              <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: '#f9fafb' }}>
                      <th style={th}>Payment</th><th style={{ ...th, textAlign: 'right' }}>Amount</th><th style={th}>What happens</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groups.flatMap((g, gi) => g.rows.map((r, ri) => {
                      const os = OUTCOME_STYLE[r.outcome] || OUTCOME_STYLE.removed;
                      return (
                        <tr key={`${gi}-${ri}`}>
                          <td style={{ ...td, fontWeight: 600 }}>{r.label}</td>
                          <td style={{ ...td, textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap' }}>{money(r.amount)}</td>
                          <td style={td}>
                            <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: os.bg, color: os.color, marginBottom: 4 }}>{os.label}</span>
                            <div style={{ color: '#4b5563' }}>{r.text}</div>
                          </td>
                        </tr>
                      );
                    }))}
                  </tbody>
                </table>
              </div>
            )}
            {hasDirect && (
              <div style={{ marginTop: 8, padding: '8px 12px', borderRadius: 8, background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', fontSize: 12.5 }}>
                Cancelled receipts will no longer show in your books. If you are keeping or refunding that money, record it separately.
              </div>
            )}

            {/* B. Everything else */}
            <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', margin: '18px 0 6px' }}>2. Everything else that changes</div>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: '#374151', lineHeight: 1.7 }}>
              <li>
                <strong>{lines.length}</strong> order line{lines.length === 1 ? '' : 's'} released — they can be billed again
                {lines.length > 0 && (
                  <span style={{ color: '#6b7280' }}> ({lines.slice(0, 4).map(l => `${l.orderNumber}: ${l.product}`).join(', ')}{lines.length > 4 ? ` +${lines.length - 4} more` : ''})</span>
                )}
              </li>
              <li>Bill number <strong>{preview.originalBillNumber}</strong> becomes free to reuse. This bill is renamed <strong>{preview.newBillNumber}</strong>.</li>
              <li>The bill is removed from the customer ledger.</li>
            </ul>

            {/* C. Reason */}
            <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', margin: '18px 0 6px' }}>3. Reason (required)</div>
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={500}
              placeholder="e.g. Wrong rate / duplicate invoice / customer cancelled"
              style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, outline: 'none', resize: 'vertical', fontFamily: 'inherit' }} />
          </>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
          <button onClick={onClose} disabled={saving}
            style={{ padding: '9px 20px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#f9fafb', fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>
            Keep Bill
          </button>
          <button onClick={confirm} disabled={!canConfirm}
            style={{ padding: '9px 22px', borderRadius: 8, border: 'none', background: '#dc2626', color: '#fff', fontWeight: 700, fontSize: 13, cursor: canConfirm ? 'pointer' : 'not-allowed', opacity: canConfirm ? 1 : 0.45 }}>
            {saving ? 'Cancelling…' : 'Confirm Cancel Bill'}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── Main BillDetail Page ────────────────────────────────────────── */
const BillDetail = () => {
  const { billId }  = useParams();
  const navigate    = useNavigate();
  const [bill, setBill]               = useState(null);
  const [linkedOrders, setLinked]     = useState([]);
  const [loading, setLoading]         = useState(true);
  const [payModal, setPayModal]       = useState(false);
  const [pdfModal, setPdfModal]       = useState(false);
  const [editModal, setEditModal]     = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [editReceiptId, setEditReceiptId] = useState(null);
  const [cancelModal, setCancelModal] = useState(false);
  const { isAdmin } = useAuth();

  // Open the edit popup for a payment row (find the receipt id from its receipt number if the row has none)
  const openReceiptEdit = async (entry) => {
    if (entry.receiptId) { setEditReceiptId(entry.receiptId); return; }
    try {
      const { data } = await API.get('/client-receipts', { params: { q: entry.receiptNumber, status: 'active', limit: 10 } });
      const r = (data.receipts || []).find(x => x.receiptNumber === entry.receiptNumber);
      if (r) setEditReceiptId(r.id);
      else toast.error(`Receipt ${entry.receiptNumber} was not found or is no longer active`);
    } catch { toast.error('Could not open the receipt'); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await API.get(`/bills/${billId}`);
      setBill(data.bill);
      setLinked(data.linkedOrders || []);
    } catch (err) {
      toast.error('Could not load bill');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [billId]); // eslint-disable-line

  if (loading) return <div className="so-page"><div className="so-empty">Loading…</div></div>;
  if (!bill)   return <div className="so-page"><div className="so-empty">Bill not found.</div></div>;

  const sm      = BILL_STATUS[bill.status] || BILL_STATUS.generated;
  const isCancelled = bill.status === 'cancelled';
  const cancelled   = (bill.cancelledPayments && typeof bill.cancelledPayments === 'object') ? bill.cancelledPayments : null;
  const balance = Math.max(0, Number(bill.grandTotal) - Number(bill.amountReceived));
  const entries = Array.isArray(bill.paymentEntries) ? bill.paymentEntries : [];
  const items   = Array.isArray(bill.consolidatedItems) ? bill.consolidatedItems : [];

  return (
    <div className="so-page">

      {/* Header */}
      <div className="so-page-head" style={{ alignItems: 'flex-start' }}>
        <div>
          <button onClick={() => navigate('/admin/billing?tab=all')}
            style={{ background: 'none', border: 'none', color: 'var(--primary)', fontWeight: 600, fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, marginBottom: 8 }}>
            <FiArrowLeft size={14} /> Back to Billing
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <h2 style={{ margin: 0 }}>{bill.billNumber}</h2>
            <span className="so-badge" style={{ background: sm.bg, color: sm.color, fontSize: 13 }}>{sm.label}</span>
            {isCancelled && bill.originalBillNumber && (
              <span style={{ fontSize: 12.5, color: '#6b7280' }}>Original number <strong>{bill.originalBillNumber}</strong> is free to reuse</span>
            )}
          </div>
          <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>
            {bill.companyName || bill.customerName}
            {bill.companyName && <span style={{ marginLeft: 6, color: '#9ca3af' }}>· {bill.customerName}</span>}
            <span style={{ marginLeft: 12 }}>Bill Date: {fmt(bill.billDate)}</span>
            {bill.dueDate && <span style={{ marginLeft: 12, color: balance > 0 && new Date(bill.dueDate) < new Date() ? '#dc2626' : '#6b7280' }}>Due: {fmt(bill.dueDate)}{balance > 0 && new Date(bill.dueDate) < new Date() ? ' ⚠️' : ''}</span>}
          </div>
        </div>

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {!isCancelled && (
          <button onClick={() => setEditModal(true)}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '1px solid #d1d5db', background: '#f9fafb', fontSize: 13, fontWeight: 600, cursor: 'pointer', color: '#374151' }}>
            <FiEdit2 size={14} /> Edit Bill
          </button>
          )}
          {bill.pdfUrl ? (
            <>
              {!isCancelled && (
              <button onClick={() => setPdfModal(true)}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '1px solid #d1d5db', background: '#f9fafb', fontSize: 13, fontWeight: 600, cursor: 'pointer', color: '#374151' }}>
                <FiUpload size={14} /> Replace PDF
              </button>
              )}
              <a href={resolvePdfUrl(bill.pdfUrl)} target="_blank" rel="noreferrer"
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '1px solid #3b82f6', color: '#3b82f6', background: '#eff6ff', fontSize: 13, fontWeight: 600, textDecoration: 'none' }}>
                <FiFileText size={14} /> Preview Bill
              </a>
            </>
          ) : !isCancelled && (
            <button onClick={() => setPdfModal(true)}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '1px solid #d1d5db', background: '#f9fafb', fontSize: 13, fontWeight: 600, cursor: 'pointer', color: '#374151' }}>
              <FiUpload size={14} /> Upload PDF
            </button>
          )}
          {!isCancelled && bill.status !== 'paid' && (
            <button onClick={() => setPayModal(true)}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: 'none', background: 'var(--primary)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              <FiPlus size={14} /> Record Payment
            </button>
          )}
          {entries.length > 0 && (
            <button onClick={() => setShowHistory(h => !h)}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '1px solid #d1d5db', background: '#f9fafb', fontSize: 13, fontWeight: 600, cursor: 'pointer', color: '#374151' }}>
              <FiList size={14} /> History ({entries.length})
            </button>
          )}
          {isAdmin && !isCancelled && (
            <button onClick={() => setCancelModal(true)}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 8, border: '1px solid #fca5a5', background: '#fef2f2', fontSize: 13, fontWeight: 600, cursor: 'pointer', color: '#b91c1c' }}>
              <FiSlash size={14} /> Cancel Bill
            </button>
          )}
        </div>
      </div>

      {/* Cancelled banner + payment history (the only place cancelled receipts show) */}
      {isCancelled && (
        <div style={{ ...card, border: '1px solid #fecaca', background: '#fef2f2' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 700, color: '#991b1b', marginBottom: 6 }}>
            <FiSlash size={15} /> This bill was cancelled
          </div>
          <div style={{ fontSize: 13, color: '#7f1d1d', lineHeight: 1.7 }}>
            <div><strong>Reason:</strong> {bill.cancelReason || '—'}</div>
            <div><strong>Cancelled on:</strong> {bill.cancelledAt ? new Date(bill.cancelledAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}</div>
          </div>
          {cancelled && Array.isArray(cancelled.items) && cancelled.items.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#991b1b', textTransform: 'uppercase', marginBottom: 6 }}>Payment history at the time of cancellation</div>
              <div style={{ background: '#fff', border: '1px solid #fecaca', borderRadius: 8, overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 560 }}>
                  <thead>
                    <tr style={{ background: '#fff5f5' }}>
                      {['Payment', 'Amount', 'What happened'].map(h => (
                        <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {cancelled.items.map((it, i) => {
                      const os = OUTCOME_STYLE[it.outcome] || OUTCOME_STYLE.removed;
                      return (
                        <tr key={i} style={{ borderTop: '1px solid #f3f4f6' }}>
                          <td style={{ padding: '9px 12px', fontWeight: 600 }}>{it.label}</td>
                          <td style={{ padding: '9px 12px', fontWeight: 700, whiteSpace: 'nowrap' }}>{money(it.amount)}</td>
                          <td style={{ padding: '9px 12px' }}>
                            <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: os.bg, color: os.color, marginRight: 8 }}>{os.label}</span>
                            <span style={{ color: '#4b5563' }}>{it.text}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Payment summary strip */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'Grand Total',    value: money(bill.grandTotal),    color: '#111' },
          { label: entries.some(e => e.source === 'tds') ? 'Settled (incl. TDS)' : 'Amount Received', value: money(bill.amountReceived), color: '#16a34a' },
          ...(entries.some(e => e.source === 'tds')
            ? [{ label: 'of which TDS', value: money(entries.filter(e => e.source === 'tds').reduce((t, e) => t + Number(e.amount || 0), 0)), color: '#b45309' }]
            : []),
          { label: 'Balance',        value: money(balance),            color: balance > 0 ? '#dc2626' : '#16a34a' },
          { label: 'Orders Covered', value: `${Array.isArray(bill.linkedOrderIds) ? bill.linkedOrderIds.length : 0} order(s)`, color: '#374151' },
        ].map(s => (
          <div key={s.label} style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '14px 16px', textAlign: 'center' }}>
            <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 }}>{s.label}</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: s.color }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Payment history (inline expandable) */}
      {showHistory && entries.length > 0 && (
        <div style={{ ...card, marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 14 }}>Payment History</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 620 }}>
            <thead>
              <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                {['#', 'Date', 'Receipt No', 'Mode', 'Account', 'Amount', 'Note', ''].map(h => (
                  <th key={h} style={{ padding: '9px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {entries.map((e, i) => (
                <tr key={i} style={{ borderBottom: '1px solid #f3f4f6' }}>
                  <td style={{ padding: '10px 12px', color: '#9ca3af' }}>{i + 1}</td>
                  <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>{fmt(e.date)}</td>
                  <td style={{ padding: '10px 12px' }}>
                    {e.receiptNumber
                      ? <span style={{ padding: '3px 10px', borderRadius: 6, fontSize: 12, fontWeight: 700, background: '#ede9fe', color: '#6d28d9', whiteSpace: 'nowrap', letterSpacing: '0.02em' }}>{e.receiptNumber}</span>
                      : <span style={{ color: '#9ca3af' }}>—</span>
                    }
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    {e.source === 'tds'
                      ? <span style={{ padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: '#fef3c7', color: '#b45309' }}>TDS</span>
                      : <span style={{ padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: '#eff6ff', color: '#2563eb' }}>{e.mode || 'Cash'}</span>}
                  </td>
                  <td style={{ padding: '10px 12px', color: '#374151' }}>{e.source === 'tds' ? <span style={{ color: '#9ca3af' }}>not bank money</span> : (e.bank_account_label || '—')}</td>
                  <td style={{ padding: '10px 12px', fontWeight: 700, color: e.source === 'tds' ? '#b45309' : '#16a34a' }}>{money(e.amount)}</td>
                  <td style={{ padding: '10px 12px', color: '#6b7280' }}>
                    {e.source === 'tds'
                      ? `Deducted by customer${e.tdsPercent != null ? ` · ${Number(Number(e.tdsPercent).toFixed(2))}%` : ''}${e.tdsSection ? ` · ${e.tdsSection}` : ''}${e.note ? ` · ${e.note}` : ''}`
                      : (e.note || '—')}
                  </td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {isAdmin && e.receiptNumber && (
                      <button onClick={() => openReceiptEdit(e)}
                        style={{ padding: '4px 10px', background: '#eef2ff', color: '#4f46e5', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <FiEdit2 size={11} /> Edit
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>

        {/* Left — Consolidated items */}
        <div style={{ ...card, gridColumn: '1 / -1' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 14 }}>
            Consolidated Items
          </div>
          <div className="so-table-scroll">
            <table className="so-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Product</th>
                  <th>Total Qty</th>
                  <th>Unit Price</th>
                  <th>Tax %</th>
                  <th>Line Total</th>
                  <th>From Orders</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, i) => (
                  <tr key={i}>
                    <td className="so-muted">{i + 1}</td>
                    <td className="so-strong">{item.product}</td>
                    <td>{item.totalQty} {item.unit && <span className="so-muted">{item.unit}</span>}</td>
                    <td>{money(item.unitPrice)}</td>
                    <td className="so-muted">{item.taxPercent ?? 0}%</td>
                    <td className="so-strong">{money(item.lineTotal)}</td>
                    <td style={{ fontSize: 11, color: '#6b7280' }}>
                      {Array.isArray(item.sourceOrders)
                        ? item.sourceOrders.map(s => `${s.orderNumber}(×${s.qty})`).join(', ')
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Totals row */}
          <div style={{ display: 'flex', gap: 24, justifyContent: 'flex-end', fontSize: 13, padding: '12px 4px 0', borderTop: '1px solid #f3f4f6', marginTop: 4 }}>
            <span style={{ color: '#374151' }}>Taxable: <strong>{money(bill.taxableAmount)}</strong></span>
            <span style={{ color: '#6b7280' }}>Tax: <strong>{money(bill.taxAmount)}</strong></span>
            {Number(bill.roundOff) !== 0 && (
              <span style={{ color: '#6b7280' }}>Round Off: <strong>{Number(bill.roundOff) > 0 ? '+' : ''}{money(bill.roundOff)}</strong></span>
            )}
            <span style={{ fontSize: 15 }}>Grand Total: <strong>{money(bill.grandTotal)}</strong></span>
          </div>
        </div>

        {/* Customer details */}
        <div style={card}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 12 }}>Customer Details</div>
          {[
            ['Name',    bill.customerName],
            ['Company', bill.companyName],
            ['Phone',   bill.customerPhone],
            ['Email',   bill.customerEmail],
            ['GST No.', bill.gstNo],
            ['Address', bill.companyAddress],
          ].filter(([, v]) => v).map(([label, value]) => (
            <div key={label} style={{ display: 'flex', gap: 10, marginBottom: 8, fontSize: 13 }}>
              <span style={{ color: '#6b7280', minWidth: 70, fontWeight: 600 }}>{label}</span>
              <span style={{ color: '#111' }}>{value}</span>
            </div>
          ))}
        </div>

        {/* Linked orders */}
        <div style={card}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 12 }}>
            Linked Orders ({linkedOrders.length})
          </div>
          {linkedOrders.length === 0 ? (
            <div style={{ color: '#9ca3af', fontSize: 13 }}>No linked orders found.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {linkedOrders.map(o => (
                <div key={o.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: '#f8faff', borderRadius: 8, border: '1px solid #dbeafe' }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#1d4ed8' }}>{o.orderNumber}</div>
                    <div style={{ fontSize: 11, color: '#6b7280' }}>{fmt(o.orderDate)} · {money(o.grandTotal)}</div>
                  </div>
                  <button onClick={() => navigate(`/admin/sales-orders/${o.id}`)}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', display: 'flex', alignItems: 'center' }}>
                    <FiExternalLink size={14} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Purchase Order Details */}
        {(bill.poNumber || bill.poDate || bill.poPdfUrl) && (
          <div style={{ ...card, gridColumn: '1 / -1' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 12 }}>Purchase Order</div>
            <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'center', fontSize: 13 }}>
              {bill.poNumber && (
                <div style={{ display: 'flex', gap: 8 }}>
                  <span style={{ color: '#6b7280', fontWeight: 600, minWidth: 80 }}>PO Number</span>
                  <span style={{ color: '#111', fontWeight: 700, background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 6, padding: '2px 10px', letterSpacing: '0.02em' }}>{bill.poNumber}</span>
                </div>
              )}
              {bill.poDate && (
                <div style={{ display: 'flex', gap: 8 }}>
                  <span style={{ color: '#6b7280', fontWeight: 600, minWidth: 60 }}>PO Date</span>
                  <span style={{ color: '#111' }}>{fmt(bill.poDate)}</span>
                </div>
              )}
              {bill.poPdfUrl && (
                <a href={resolvePdfUrl(bill.poPdfUrl)} target="_blank" rel="noreferrer"
                  style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 14px', borderRadius: 8, border: '1px solid #8b5cf6', color: '#7c3aed', background: '#f5f3ff', fontSize: 13, fontWeight: 600, textDecoration: 'none' }}>
                  <FiFileText size={13} /> View PO PDF
                </a>
              )}
            </div>
          </div>
        )}

        {/* Notes */}
        {bill.notes && (
          <div style={{ ...card, gridColumn: '1 / -1' }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 8 }}>Notes</div>
            <div style={{ fontSize: 13, color: '#374151', whiteSpace: 'pre-wrap' }}>{bill.notes}</div>
          </div>
        )}
      </div>

      {/* Modals */}
      {editModal && (
        <EditBillModal bill={bill} onClose={() => setEditModal(false)}
          onSaved={(updated) => { setBill(updated); setEditModal(false); }} />
      )}
      {cancelModal && (
        <CancelBillModal bill={bill} onClose={() => setCancelModal(false)}
          onCancelled={() => { setCancelModal(false); load(); }} />
      )}
      {payModal && (
        <RecordPaymentModal bill={bill} onClose={() => setPayModal(false)}
          onSaved={() => { API.get(`/bills/${bill.id}`).then(({ data }) => setBill(data.bill)).catch(() => {}); setPayModal(false); }} />
      )}
      {editReceiptId && (
        <EditReceiptModal
          receiptId={editReceiptId}
          onClose={() => setEditReceiptId(null)}
          onSaved={() => { API.get(`/bills/${billId}`).then(({ data }) => { setBill(data.bill); setLinked(data.linkedOrders || []); }).catch(() => {}); }}
        />
      )}
      {pdfModal && (
        <PdfUploadModal bill={bill} onClose={() => setPdfModal(false)}
          onUploaded={(updated) => { setBill(updated); setPdfModal(false); }} />
      )}
    </div>
  );
};

export default BillDetail;